import * as _ from 'lodash';

import { ZoneModel } from '../zone-models/zone.model';

import { SubZoneModel } from '../zone-models/sub-zone.model';
import { ZoneTypeModel } from '../zone-models/zone-type.model';
import { CalibrationUsesLabelModel } from '../calibration-uses-label.model';
import { CalibrationUsesTypeModel } from '../calibration-uses-type.model';
import { ZoneCombinationModel } from '../zone-models/zone-combination.model';
import { AllowedUseModel } from '../../common-models/allowed-use.model';

import { UseCapacityTable } from '../../tables/use-capacity-table.model';
import { UsesTableModel } from '../../tables/uses-table.model';
import { GeneralTable } from '../../tables/general-table.model';
import { ParkingTable } from '../../tables/parking-table.model';
import { TableType, AttributesStatisticCollectionNames, ImportTemplateType } from '../../../../shared/enums';
import { OverlayModel } from '../overlay/overlay.model';
import { CalibrationSetType, CalibrationSetCollectionName } from '../../../../shared/enums/calibration-set-type';
import { OverlayType } from '../../../../shared/enums/overlay-type.enum';
import { BonusTable } from '../../tables/bonus-table.model';
import IZoningCode = ZoneIQCalibration.IZoningCode;
import IAllowedUse = ZoneIQCalibration.IAllowedUse;
import { AttributeModel } from '../../common-models/attribute.model';
import ICalibrationSet = ZoneIQCalibration.ICalibrationSet;
import { IZoneCombinationModel } from 'src/app/interfaces/zone-combination-model.interface';
import {
    IBaseTable,
    ICalibrationSetTables,
    IUsesTableModel,
    CalibrationSetTableKey
} from 'src/app/interfaces/calibration-set-table.interface';
import {
    IAttributesStatistics,
    IZoningCodeModel,
    IZoningCodeStatus
} from 'src/app/interfaces/zoning-code-model.interface';
import { IOverlayModel } from 'src/app/interfaces/overlay-model.interface';
import { ICalibrationSetModel } from 'src/app/interfaces/calibration-set-model.interface';
import ICalibrationSetAttributesStatistic = ZoneIQCalibration.ICalibrationSetAttributesStatistic;
import IAttributesStatisticFlags = ZoneIQCalibration.IAttributesStatisticFlags;
import ICalibrationUsesTypeLabel = ZoneIQCalibration.ICalibrationUsesTypeLabel;
import IUseClass = ZoneIQCalibration.IUseClass;
import IZoningCodeValidationResult = ZoneIQCalibration.IZoningCodeValidationResult;
import ICity = ZoneIQCalibration.ICity;
import IGridicsUseTag = ZoneIQCalibration.IGridicsUseTag;
import ICalibrationSetTableFieldAttribute = ZoneIQCalibration.ICalibrationSetTableFieldAttribute;
import { IOverlayTypologyModel } from 'src/app/interfaces/overlay-typology-model.interface';
import {
    ICalibrationSetsGroupValidationResult,
    IZCValidationResult,
    IZCValidationTableResult
} from 'src/app/interfaces/zc-validation-result.interface';
import { getUnitSystemStr, UnitSystem, UnitSystemStr } from 'src/shared/enums/unit-system-enum';
import { CUL_MAX_LABEL_LENGTH, NA_VALUE } from 'src/app/constants';
import { ValidationService } from 'src/app/core/services/validation.service';
import { CalibrationSetTableFieldAttribute } from '../calibration-set-table-field-attribute.model';

const properties = [
    'Id',
    'RevisionId',
    'Name',
    'UserId',
    'CreatedOn',
    'ModifiedOn',
    'SynchronizedOn',
    'Description',
    'GridicsId',
    'AdditionalPlaceIds',
    'EffectiveDate',
    'CityId',
    'Priority',
    'CloneId',
    'CreatedBy',
    'ParentId',
    'DisclaimerStatusId',
    'Disclaimer',
    'IsTemporary',
    'ActualVersion',
    'ParentActualVersion',
    'UnitSystem',
    'status',
    'Municipality',
    'ZoningRegulationName',
    'ZoningRegulationLink',
    'importValidationResult',
    'IsPublishingRequired'
];

export class ZoningCodeModel implements IZoningCodeModel {
    Id: number;
    RevisionId: number;
    Name: string;
    UserId?: any;
    CreatedOn: Date;
    ModifiedOn: Date;
    SynchronizedOn: Date;
    Description: string;
    GridicsId: string;
    AdditionalPlaceIds: string;
    EffectiveDate: string;
    CityId: number;
    Priority: number;
    CloneId: number;
    CreatedBy?: any;
    ParentId?: any;
    DisclaimerStatusId: number;
    Disclaimer: string;
    IsTemporary: boolean;
    ActualVersion: number;
    ParentActualVersion: number;
    UnitSystem: UnitSystem;
    ZoningRegulationName?: string;
    ZoningRegulationLink?: string;
    IsPublishingRequired?: boolean;

    Zones?: ZoneModel[];
    SubZones?: SubZoneModel[];
    ZoneTypes?: ZoneTypeModel[];
    CalibrationUsesLabels?: CalibrationUsesLabelModel[];
    CalibrationUsesTypes?: CalibrationUsesTypeModel[] = [];
    ZoneCombinations?: ZoneCombinationModel[];
    CalibrationUsesTypeLabels?: ICalibrationUsesTypeLabel[];

    Overlays?: IOverlayModel[];
    OverlaysOnly?: IOverlayModel[];
    Typologies?: IOverlayModel[];

    OverlaysTypologies: IOverlayTypologyModel[] = [];

    tables: ICalibrationSetTables;
    attributes: IAttributesStatistics = {};
    status?: IZoningCodeStatus;
    areZonesTablesInitialized = false;

    AllowedUses?: IAllowedUse[];
    UseClasses?: IUseClass[];
    GridicsUseTags?: IGridicsUseTag[];

    Municipality?: ICity;

    CalibrationSetTableFieldsAttributes: ICalibrationSetTableFieldAttribute[] = [];

    unitSystemStr?: UnitSystemStr;

    validationResult: Map<boolean, IZCValidationResult> = new Map();
    importValidationResult?: IZoningCodeValidationResult;

    private static readonly calibrationSetTableKeys: CalibrationSetTableKey[] = [
        CalibrationSetCollectionName.CALIBRATION_ZONE_COMBINATION,
        CalibrationSetCollectionName.CALIBRATION_OVERLAY,
        CalibrationSetCollectionName.CALIBRATION_TYPOLOGY
    ];

    private isCalibrationSetTableKey(value: string): value is CalibrationSetTableKey {
        return ZoningCodeModel.calibrationSetTableKeys.includes(value as CalibrationSetTableKey);
    }

    get isChanged() {
        const calibrationSets = Object.keys(
            this.tables
        ).filter((calibrationSet): calibrationSet is CalibrationSetTableKey =>
            this.isCalibrationSetTableKey(calibrationSet)
        );
        const zcmb = this.ZoneCombinations.filter(zone => zone.isChanged).length;
        const useLabelsLength = this.CalibrationUsesLabels.filter(label => label.isChanged).length;

        const overlayChanges = this.Overlays.filter(overlay => {
            if (overlay.BuildingCalibrationType !== OverlayType.BUILDING_TYPOLOGY) {
                return overlay.OverlayTypology.filter(overTypo => overTypo.isChanged).length;
            }
        }).length;

        const tables = calibrationSets.filter((calibrationSet: CalibrationSetTableKey) => {
            const calibrationSetTable = this.tables[calibrationSet];
            return (
                calibrationSetTable?.parkingTable?.isChanged ||
                calibrationSetTable?.useCapacityTable?.isChanged ||
                calibrationSetTable?.generalTable?.isChanged ||
                calibrationSetTable?.bonusTable?.isChanged ||
                calibrationSetTable?.usesTable?.isChanged
            );
        }).length;

        return zcmb || tables || overlayChanges || useLabelsLength;
    }

    constructor(data: IZoningCode) {
        Object.assign(this, _.pick(data, properties));
    }

    static factory(data: IZoningCode): IZoningCodeModel {
        const zoningCode = new ZoningCodeModel(data);

        if (!data) {
            return zoningCode;
        }

        zoningCode.Zones = data.Zones ? data.Zones.map(e => ZoneModel.factory(e)) : [];
        zoningCode.SubZones = data.SubZones ? data.SubZones.map(e => SubZoneModel.factory(e)) : [];
        zoningCode.ZoneTypes = data.ZoneTypes ? data.ZoneTypes.map(e => ZoneTypeModel.factory(e)) : [];

        zoningCode.setCalibrationUsesTypeLabels(data.CalibrationUsesTypeLabels);

        ZoningCodeModel.initCalibrationUsesData(zoningCode, data.CalibrationUsesLabels, data.CalibrationUsesTypes);

        zoningCode.AllowedUses = data.AllowedUses ? data.AllowedUses.map(e => new AllowedUseModel(e)) : [];
        zoningCode.AllowedUses.unshift({ Id: NA_VALUE, Label: 'N/A', UseConditionId: NA_VALUE } as IAllowedUse);

        zoningCode.UseClasses = data.UseClasses || [];
        zoningCode.GridicsUseTags = data.GridicsUseTags || [];

        zoningCode.Overlays = data.Overlays
            ? data.Overlays.map((e, _i, overlayArr) =>
                  OverlayModel.factory(e, overlayArr, zoningCode, data.CalibrationSetsAttributesStatistics)
              )
            : [];

        zoningCode.OverlaysOnly = zoningCode.Overlays.filter(
            overlay => overlay.BuildingCalibrationType !== OverlayType.BUILDING_TYPOLOGY
        );

        zoningCode.Typologies = zoningCode.Overlays.filter(overlay => overlay.isBuildingTypology);

        zoningCode.ZoneCombinations = data.ZoneCombinations
            ? data.ZoneCombinations.map(zoneCombination =>
                  ZoneCombinationModel.factory(zoneCombination, zoningCode, data.CalibrationSetsAttributesStatistics)
              )
            : [];

        zoningCode.tables = {
            zones: this.getCalibrationViewModels(zoningCode),
            overlays: this.getCalibrationViewModels(zoningCode),
            typologies: this.getCalibrationViewModels(zoningCode)
        };

        zoningCode.attributes.ZoningCodeAttributesStatistics = ZoningCodeModel.getAttributesStatistic(
            data.ZoningCodeAttributesStatistics
        );
        zoningCode.attributes.CalibrationSetTableFieldsAttributesStatistics = ZoningCodeModel.getAttributesStatistic(
            data.CalibrationSetTableFieldsAttributesStatistics
        );

        zoningCode.OverlaysTypologies = zoningCode.OverlaysOnly.reduce((acc, overlay) => {
            acc = acc.concat(overlay.OverlayTypology);
            return acc;
        }, []);

        zoningCode.CalibrationSetTableFieldsAttributes = _.get(
            data,
            'CalibrationSetTableFieldsAttributes',
            []
        ).map(cstfa => CalibrationSetTableFieldAttribute.factory(cstfa));

        zoningCode.initAllCalibrationSetTables();

        zoningCode.unitSystemStr = getUnitSystemStr(zoningCode.UnitSystem);

        return zoningCode;
    }

    static getAttributesStatistic(attributes: IAttributesStatisticFlags[]) {
        const result = new Map<string, IAttributesStatisticFlags>();
        if (!_.get(attributes, 'length')) {
            return result;
        }
        attributes.forEach(attribute => {
            const key = AttributeModel.getIdentifier(attribute);
            const keyExists = result.has(key);
            const value: IAttributesStatisticFlags = keyExists
                ? result.get(key)
                : {
                      hasAssumptions: false,
                      hasComments: false,
                      hasLinks: false
                  };
            value.hasAssumptions = value.hasAssumptions || attribute.hasAssumptions;
            value.hasComments = value.hasComments || attribute.hasComments;
            value.hasLinks = value.hasLinks || attribute.hasLinks;
            if (!keyExists) {
                result.set(key, value);
            }
        });
        return result;
    }

    static initCalibrationUsesData(zoningCode: IZoningCode, usesLabels: any[], usesTypes?: any[]) {
        zoningCode.CalibrationUsesTypes = usesTypes
            ? usesTypes.map(e => CalibrationUsesTypeModel.factory(e))
            : zoningCode.CalibrationUsesTypes;
        zoningCode.CalibrationUsesLabels = usesLabels
            ? usesLabels.map(e => CalibrationUsesLabelModel.factory(e, zoningCode.CalibrationUsesTypes))
            : [];

        const calibrationUsesTypes = new Map<number, CalibrationUsesTypeModel>();
        zoningCode.CalibrationUsesTypes.forEach(el => {
            const cutLabel = zoningCode.CalibrationUsesTypeLabels.find(cutl => cutl.CalibrationUsesTypeId === el.Id);
            el.Name = _.get(cutLabel, 'Label', el.Name);
            calibrationUsesTypes.set(el.Id, el as CalibrationUsesTypeModel);
        });
        zoningCode.CalibrationUsesLabels.forEach(label => {
            const useType = calibrationUsesTypes.get(label.CalibrationUsesTypeId);
            if (!useType) {
                console.warn(
                    `Use Type with id ${label.CalibrationUsesTypeId} is not found for label ${label.Label}. Please check the consistency of Calibration Uses Types and Labels data.`
                ); // eslint-disable-line no-console
                return;
            }
            useType.addLabel(label);
        });
    }

    static getCalibrationViewModels(zoningCode: IZoningCodeModel) {
        return {
            usesTable: new UsesTableModel(zoningCode.CalibrationUsesTypes),
            generalTable: new GeneralTable(),
            parkingTable: new ParkingTable(),
            useCapacityTable: new UseCapacityTable(),
            bonusTable: new BonusTable()
        };
    }

    initCalibrationSetTable(
        tableType: TableType,
        calibrationSet: CalibrationSetCollectionName
    ): IBaseTable | IUsesTableModel {
        const table = this.getCalibrationSetTableByType(tableType, calibrationSet);
        switch (calibrationSet) {
            case CalibrationSetCollectionName.CALIBRATION_ZONE_COMBINATION:
                table.init(this.ZoneCombinations);
                break;
            case CalibrationSetCollectionName.CALIBRATION_OVERLAY:
                const overlayEntities = this.Overlays.filter(
                    overlay => overlay.BuildingCalibrationType !== OverlayType.BUILDING_TYPOLOGY
                );
                table.init(overlayEntities);
                break;
            case CalibrationSetCollectionName.CALIBRATION_TYPOLOGY:
                const typologyEntities = this.Overlays.filter(
                    overlay => overlay.BuildingCalibrationType === OverlayType.BUILDING_TYPOLOGY
                );
                table.init(typologyEntities);
                break;
        }
        return table;
    }

    getCalibrationSetTableByType(
        tableType: TableType,
        calibrationSet: CalibrationSetCollectionName
    ): IBaseTable | IUsesTableModel {
        if (!this.tables || !this.isCalibrationSetTableKey(calibrationSet) || !this.tables[calibrationSet]) {
            return null;
        }

        const calibrationSetTable = this.tables[calibrationSet];

        switch (tableType) {
            case TableType.CALIBRATION_GENERAL:
                return calibrationSetTable.generalTable;
            case TableType.CALIBRATION_USES:
                return calibrationSetTable.usesTable;
            case TableType.CALIBRATION_PARKING:
                return calibrationSetTable.parkingTable;
            case TableType.CALIBRATION_USE_CAPACITY:
                return calibrationSetTable.useCapacityTable;
            case TableType.CALIBRATION_BONUS:
                return calibrationSetTable.bonusTable;
            default:
                return null;
        }
    }

    isValid(ignoreRequiredFields: boolean = false) {
        return this.getCachedValidationResult(ignoreRequiredFields).isValid;
    }

    getValidationResults(ignoreRequiredFields: boolean = false): IZCValidationResult {
        const importType = this.importValidationResult?.importType as ImportTemplateType;
        const result: IZCValidationResult = {};
        const calibrationSets = Object.keys(
            this.tables
        ).filter((calibrationSet): calibrationSet is CalibrationSetTableKey =>
            this.isCalibrationSetTableKey(calibrationSet)
        );
        calibrationSets.forEach((calibrationSet: CalibrationSetTableKey) => {
            const calibrationSetResult: ICalibrationSetsGroupValidationResult = {};
            const calibrationSetTable = this.tables[calibrationSet];

            const ignoreRequiredFieldsExceptZone =
                calibrationSet !== CalibrationSetCollectionName.CALIBRATION_ZONE_COMBINATION;
            calibrationSetResult.ignoreRequiredFieldsExceptZone = ignoreRequiredFieldsExceptZone;

            calibrationSetResult.generalTable = {
                isValid: calibrationSetTable.generalTable.isValid(
                    ignoreRequiredFields || ignoreRequiredFieldsExceptZone
                ),
                validations: calibrationSetTable.generalTable.getValidationResults(
                    ignoreRequiredFields || ignoreRequiredFieldsExceptZone
                )
            };
            calibrationSetResult.usesTable = {
                isValid: calibrationSetTable.usesTable.isValid,
                validations: calibrationSetTable.usesTable.getValidationResults(
                    ignoreRequiredFields || ignoreRequiredFieldsExceptZone
                )
            };
            calibrationSetResult.bonusTable = {
                isValid: calibrationSetTable.bonusTable.isValid(),
                validations: calibrationSetTable.bonusTable.getValidationResults(
                    ignoreRequiredFields || ignoreRequiredFieldsExceptZone
                )
            };
            calibrationSetResult.parkingTable = {
                isValid: calibrationSetTable.parkingTable.isValid(
                    ignoreRequiredFields || ignoreRequiredFieldsExceptZone
                ),
                validations: calibrationSetTable.parkingTable.getValidationResults(
                    ignoreRequiredFields || ignoreRequiredFieldsExceptZone
                )
            };
            calibrationSetResult.useCapacityTable = {
                isValid: calibrationSetTable.useCapacityTable.isValid(
                    ignoreRequiredFields || ignoreRequiredFieldsExceptZone
                ),
                validations: calibrationSetTable.useCapacityTable.getValidationResults(
                    ignoreRequiredFields || ignoreRequiredFieldsExceptZone
                )
            };

            calibrationSetResult.isValid =
                calibrationSetResult.generalTable.isValid &&
                calibrationSetResult.usesTable.isValid &&
                calibrationSetResult.bonusTable.isValid &&
                calibrationSetResult.parkingTable.isValid &&
                calibrationSetResult.useCapacityTable.isValid;

            result[calibrationSet] = calibrationSetResult;
        });

        result.calibrationGeneral = (
            this.importValidationResult?.zoneCombinations?.calibrationGeneralUnknownDBFieldNames || []
        ).map(fieldName => {
            return {
                group: 'Zones',
                subGroup: 'General',
                category: 'Zoning Code',
                field: fieldName,
                error: 'Unknown field name'
            };
        });

        result.calibrationUseCapacity = (
            this.importValidationResult?.zoneCombinations?.useCapacityUnknownDBFieldNames || []
        ).map(fieldName => {
            return {
                group: 'Zones',
                subGroup: 'Use Capacity',
                category: 'Zoning Code',
                field: fieldName,
                error: 'Unknown field name'
            };
        });

        result.unknownEntities = [];
        result.overlayEntities = [];

        if (
            ![ImportTemplateType.ZONE_DESCRIPTIONS, ImportTemplateType.CALIBRATION_SET_TABLE_FIELD_ATTRIBUTES].includes(
                importType
            )
        ) {
            result.zoneCombinations = ValidationService.getSimpleEntitiesValidations(
                this.ZoneCombinations,
                'isExisting',
                'Name',
                'Zoning Code',
                'Zones',
                'Zone is not registered in Zoning Code'
            );

            result.zoneCombinations = result.zoneCombinations.concat(
                ValidationService.getSimpleEntitiesValidations(
                    this.ZoneCombinations,
                    'isUnique',
                    'Name',
                    'Zoning Code',
                    'Zones',
                    'Zone is declated multiple times'
                )
            );

            result.zoneCombinations = result.zoneCombinations.concat(
                ValidationService.getSimpleEntitiesValidations(
                    this.ZoneCombinations,
                    'noSpecialCharacters',
                    'Name',
                    'Zoning Code',
                    'Zones',
                    'Zone name contains special characters'
                )
            );

            result.zoneCombinations = result.zoneCombinations.concat(
                _.compact(
                    this.ZoneCombinations.map(zcmb => {
                        return zcmb.isValidBuildingTypology
                            ? null
                            : {
                                  group: 'Zones',
                                  subGroup: 'Building Typology',
                                  category: zcmb.Name,
                                  field: 'Building Typology',
                                  error: 'Default Building Type is required'
                              };
                    })
                )
            );
        } else if (
            [ImportTemplateType.ZONE_DESCRIPTIONS, ImportTemplateType.CALIBRATION_SET_TABLE_FIELD_ATTRIBUTES].includes(
                importType
            )
        ) {
            result.zoneCombinations = ValidationService.getImportDescriptionsValidationMessages(
                this.ZoneCombinations,
                'Zone'
            );
            result.overlayEntities = ValidationService.getImportDescriptionsValidationMessages(
                this.Overlays,
                'Overlay/Typology'
            );

            if (importType === ImportTemplateType.ZONE_DESCRIPTIONS) {
                result.unknownEntities = ValidationService.getImportDescriptionsValidationMessages(
                    _.get(this.importValidationResult, 'descriptionsImport.unknownEntities', []),
                    'Unknown Entity'
                );
            } else if (importType === ImportTemplateType.CALIBRATION_SET_TABLE_FIELD_ATTRIBUTES) {
                result.unknownEntities = ValidationService.getImportDescriptionsValidationMessages(
                    _.get(this.importValidationResult, 'calibrationSetTableFieldAttributesImport.unknownEntities', []),
                    'Unknown Entity'
                )
                    .concat(
                        ValidationService.getUnknownTableFieldsValidationMessages(
                            _.get(
                                this.importValidationResult,
                                'calibrationSetTableFieldAttributesImport.unknownTableFields',
                                []
                            )
                        )
                    )
                    .concat(
                        ValidationService.getCalibrationSetTableFieldsAttributesValidationMessages(
                            this.CalibrationSetTableFieldsAttributes
                        )
                    );
            }
        }

        result.calibrationUsesLabels = ValidationService.getSimpleEntitiesValidations(
            this.CalibrationUsesLabels,
            'isUnique',
            'extendedLabel',
            'Zoning Code',
            'Global Uses',
            'Use name is delcared multiple times'
        );

        result.calibrationUsesLabels = result.calibrationUsesLabels.concat(
            ValidationService.getSimpleEntitiesValidations(
                this.CalibrationUsesLabels,
                'isUseClassSet',
                'extendedLabel',
                'Zoning Code',
                'Use Name',
                'Use Class is empty'
            )
        );

        result.calibrationUsesLabels = result.calibrationUsesLabels.concat(
            ValidationService.getSimpleEntitiesValidations(
                this.CalibrationUsesLabels,
                'isCorrectLabelLength',
                'extendedLabel',
                'Zoning Code',
                'Global Uses',
                `Label is too long (max length: ${CUL_MAX_LABEL_LENGTH})`
            )
        );

        result.calibrationUsesLabels = result.calibrationUsesLabels.concat(
            ValidationService.getSimpleEntitiesValidations(
                this.CalibrationUsesLabels,
                'noSpecialCharacters',
                'extendedLabel',
                'Zoning Code',
                'Global Uses',
                `Use name contains special characters`
            )
        );

        result.calibrationUsesLabels = result.calibrationUsesLabels.concat(
            ValidationService.getSimpleEntitiesValidations(
                this.CalibrationUsesLabels,
                'isNotEmptyLabel',
                'extendedLabel',
                'Zoning Code',
                'Use Type',
                `Use name is empty`
            )
        );

        result.calibrationUsesLabels = result.calibrationUsesLabels.concat(
            ValidationService.getSimpleEntitiesValidations(
                this.CalibrationUsesLabels,
                'isCUTSet',
                'extendedLabel',
                'Zoning Code',
                'Use Name',
                `Use type is empty`
            )
        );

        result.calibrationUsesTypes = ValidationService.getSimpleEntitiesValidations(
            this.CalibrationUsesTypes,
            'isExisting',
            'Name',
            'Zoning Code',
            'Uses Types',
            'Uses Type is not registered in Zoning Code'
        );

        result.calibrationUsesTypes = result.calibrationUsesTypes.concat(
            ValidationService.getSimpleEntitiesValidations(
                this.CalibrationUsesTypes,
                'noSpecialCharacters',
                'Name',
                'Zoning Code',
                'Uses Types',
                'Uses Type name contains special characters'
            )
        );

        result.allowedUses = ValidationService.getSimpleEntitiesValidations(
            this.AllowedUses,
            'isExisting',
            'Name',
            'Zoning Code',
            'Allowed Uses',
            'Allowed Use is not registered in Zoning Code'
        );

        result.allowedUses = result.allowedUses.concat(
            ValidationService.getSimpleEntitiesValidations(
                this.AllowedUses,
                'noSpecialCharacters',
                'Name',
                'Zoning Code',
                'Allowed Uses',
                'Allowed Use name contains special characters'
            )
        );

        result.useClasses = ValidationService.getSimpleEntitiesValidations(
            this.UseClasses,
            'isExisting',
            'Label',
            'Zoning Code',
            'Use Classes',
            'Use Class is not registered'
        );

        result.useClasses = result.useClasses.concat(
            ValidationService.getSimpleEntitiesValidations(
                this.UseClasses,
                'noSpecialCharacters',
                'Label',
                'Zoning Code',
                'Use Classes',
                'Use Class name contains special characters'
            )
        );

        result.gridicsUseTags = ValidationService.getSimpleEntitiesValidations(
            this.GridicsUseTags,
            'isExisting',
            'GridicsUse',
            'Zoning Code',
            'Gridics Use Tags',
            'Gridics Use Tag is not registered'
        );

        result.gridicsUseTags = result.gridicsUseTags.concat(
            ValidationService.getSimpleEntitiesValidations(
                this.GridicsUseTags,
                'isNotEmptyName',
                'GridicsUse',
                'Zoning Code',
                'Gridics Use Tags',
                'Gridics Use Tag name is empty'
            )
        );

        result.gridicsUseTags = result.gridicsUseTags.concat(
            ValidationService.getSimpleEntitiesValidations(
                this.GridicsUseTags,
                'noSpecialCharacters',
                'GridicsUse',
                'Zoning Code',
                'Gridics Use Tags',
                'Gridics Use Tag name contains special characters'
            )
        );

        result.zoneCombinationCalibrationGeneral = this.ZoneCombinations.filter(
            zc => zc.CalibrationGeneral?.importValidationResult && !zc.CalibrationGeneral.importValidationResult.isValid
        ).reduce((acc, zc) => {
            const importResult = zc.CalibrationGeneral.importValidationResult;

            (importResult.incorrectTypeFieldNames || []).forEach(fieldName => {
                acc.push({
                    group: 'Zones',
                    subGroup: 'General',
                    category: zc.Name,
                    field: fieldName,
                    error: 'Incorrect field type'
                });
            });

            (importResult.notAllowedValuesFieldNames || []).forEach(fieldName => {
                acc.push({
                    group: 'Zones',
                    subGroup: 'General',
                    category: zc.Name,
                    field: fieldName,
                    error: 'Value is not allowed'
                });
            });

            return acc;
        }, [] as IZCValidationTableResult[]);

        // TODO :: inify with zoneCombinationCalibrationGeneral
        result.zoneCombinationCalibrationUseCapacity = this.ZoneCombinations.filter(
            zc =>
                zc.CalibrationOfficeCommercial?.importValidationResult &&
                !zc.CalibrationOfficeCommercial.importValidationResult.isValid
        ).reduce((acc, zc) => {
            const importResult = zc.CalibrationOfficeCommercial.importValidationResult;

            (importResult.incorrectTypeFieldNames || []).forEach(fieldName => {
                acc.push({
                    group: 'Zones',
                    subGroup: 'Use Capacity',
                    category: zc.Name,
                    field: fieldName,
                    error: 'Incorrect field type'
                });
            });

            (importResult.notAllowedValuesFieldNames || []).forEach(fieldName => {
                acc.push({
                    group: 'Zones',
                    subGroup: 'Use Capacity',
                    category: zc.Name,
                    field: fieldName,
                    error: 'Value is not allowed'
                });
            });

            return acc;
        }, [] as IZCValidationTableResult[]);

        result.isValid =
            calibrationSets.every(calibrationSet => result[calibrationSet]?.isValid) &&
            !result.zoneCombinations.length &&
            !result.zoneCombinationCalibrationGeneral.length &&
            !result.zoneCombinationCalibrationUseCapacity.length &&
            !result.calibrationUsesLabels.length &&
            !result.calibrationUsesTypes.length &&
            !result.allowedUses.length &&
            !result.useClasses.length &&
            !result.gridicsUseTags.length &&
            !result.calibrationGeneral.length &&
            !result.calibrationUseCapacity.length &&
            _.isEmpty(result.overlayEntities) &&
            _.isEmpty(result.unknownEntities);
        return result;
    }

    getCachedValidationResult(ignoreRequiredFields?: boolean): IZCValidationResult {
        if (!this.validationResult.get(ignoreRequiredFields)) {
            this.validationResult.set(ignoreRequiredFields, this.getValidationResults(ignoreRequiredFields));
        }
        return this.validationResult.get(ignoreRequiredFields);
    }

    get isValidStatus() {
        return this.status ? this.status.isValid : false;
    }

    get notAllowedUse() {
        return this.AllowedUses.find(use => !use.UseConditionId);
    }

    cancelChanges() {
        this.Overlays.forEach((overlay: IOverlayModel) => {
            overlay.cancelChanges();
        });
        this.ZoneCombinations.forEach((zcmb: IZoneCombinationModel) => {
            zcmb.cancelChanges();
        });
        this.CalibrationUsesLabels.forEach(label => {
            label.removeChanges();
        });
    }

    updateData(entityName: string, updatedEntities: any[], status?: any, toDelete = false) {
        this.status = status;

        if (!_.get(updatedEntities, 'length', 0)) {
            return;
        }

        switch (entityName) {
            case AttributesStatisticCollectionNames.ZONING_CODE_ATTRIBUTES_STATISTICS:
            case AttributesStatisticCollectionNames.CALIBRATION_SET_TABLE_FIELDS_ATTRIBUTES_STATISTICS:
                this.updateAttributesStatisticMap(entityName as keyof IAttributesStatistics, updatedEntities);
                break;
            case AttributesStatisticCollectionNames.CALIBRATION_SETS_ATTRIBUTES_STATISTICS:
                this.updateEntitiesAttributesStatistic(updatedEntities);
                break;
            case AttributesStatisticCollectionNames.CALIBRATION_USES_LABELS:
                this.updateUsesLabels(updatedEntities);
                break;
            default:
                this.updateTabsData(entityName, updatedEntities, toDelete);
                break;
        }
    }

    private updateTabsData(entityName: string, updatedEntities: any[], toDelete = false) {
        updatedEntities.forEach((updatedEntity: any) => {
            const tabName = updatedEntity.ZoneCombinationId
                ? CalibrationSetCollectionName.CALIBRATION_ZONE_COMBINATION
                : !updatedEntity.OverlayTypologyId && !updatedEntity.OverlayId
                ? CalibrationSetCollectionName.BUILDING_TYPOLOGY
                : CalibrationSetCollectionName.CALIBRATION_OVERLAY;

            // It's temporary solution while we can't distinguish updatedEntity
            // if it's just zoneCombination or Overlay not a part of calibration model approach
            if (tabName === CalibrationSetCollectionName.BUILDING_TYPOLOGY) {
                entityName = tabName;
            }

            // Like a case above when we need to specify entityName for OverlayTypology
            if (tabName === CalibrationSetCollectionName.CALIBRATION_OVERLAY && updatedEntity.TypologyId) {
                entityName = CalibrationSetCollectionName.OVERLAY_TYPOLOGY;
            }

            const notAllowedUseId = this.notAllowedUse ? this.notAllowedUse.Id : null;

            if (tabName === CalibrationSetCollectionName.CALIBRATION_OVERLAY) {
                this.Overlays.forEach(overlay => {
                    if (overlay.Id === (updatedEntity.OverlayTypologyId || updatedEntity.OverlayId)) {
                        overlay.updateData(updatedEntity, entityName, notAllowedUseId, toDelete);
                    }
                });
            } else {
                // zoneCombination & overlay have only 'Id' property, that's why added additional condition '||'
                const zoneCmb = this.ZoneCombinations.find(zone => {
                    return zone.Id === updatedEntity.ZoneCombinationId || zone.Id === updatedEntity.Id;
                });
                if (zoneCmb) {
                    zoneCmb.updateData(updatedEntity, entityName, notAllowedUseId, toDelete);
                }
            }
        });
    }

    private updateAttributesStatisticMap(
        entityName: keyof IAttributesStatistics,
        updatedEntities: ICalibrationSetAttributesStatistic[]
    ) {
        updatedEntities.forEach(updatedData => {
            const identifier = AttributeModel.getIdentifier(updatedData);
            const attributesMap = this.attributes[entityName];
            if (!attributesMap) {
                return;
            }
            if (updatedData.IsDeleted) {
                attributesMap.delete(identifier);
                return;
            }
            attributesMap.set(identifier, updatedData);
        });
    }

    private updateUsesLabels(updatedLabels: any[]) {
        updatedLabels.forEach(updatedLabel => {
            const existingUseLabel: CalibrationUsesLabelModel = this.CalibrationUsesLabels.find(
                label => label.Id === updatedLabel.Id
            );
            if (existingUseLabel) {
                // Ensures the state of the original value is updated
                existingUseLabel.updateValues(updatedLabel);
                return;
            }
            const newUseLabel = CalibrationUsesLabelModel.factory(updatedLabel, this.CalibrationUsesTypes);
            this.CalibrationUsesLabels.push(newUseLabel);

            const useType = this.CalibrationUsesTypes.find(type => type.Id === newUseLabel.CalibrationUsesTypeId);
            useType.addLabel(newUseLabel);
        });
    }

    /**
     *
     * @param {CalibrationSetType} calibrationSetType - type of calibration set entities (Zone Combination, Overlay, Typology)
     * @returns collection of Calibration Sets (Zone Combinations, Overlays, Typologies)
     */
    getCalibrationSetCollection(calibrationSetType: CalibrationSetType): ICalibrationSetModel<ICalibrationSet>[] {
        switch (calibrationSetType) {
            case CalibrationSetType.ZONE_COMBINATION:
                return this.ZoneCombinations;
            case CalibrationSetType.OVERLAY:
                return this.Overlays;
            case CalibrationSetType.TYPOLOGY:
                return this.Typologies;
            default:
                return [];
        }
    }

    getZoneCombinationById(zoneCombinationId: number): IZoneCombinationModel {
        if (!zoneCombinationId) {
            return null;
        }
        return this.ZoneCombinations.find(zCmb => zCmb.Id === zoneCombinationId);
    }

    /**
     * Updates Attributes Statistic Flags of found entities
     * @param {ICalibrationSetAttributesStatistic[]} attrStat - information about existence of attributes of specific types for entities
     */
    updateEntitiesAttributesStatistic(attrStat: ICalibrationSetAttributesStatistic[]) {
        if (!_.get(attrStat, 'length')) {
            return;
        }

        attrStat.forEach(stat => {
            const collection: any[] =
                stat.ParentType === CalibrationSetType.OVERLAY_TYPOLOGY
                    ? this.OverlaysTypologies
                    : this.getCalibrationSetCollection(stat.ParentType);
            const entity = collection.find(clbSet => clbSet.currentState.Id === stat.ParentId);
            if (entity) {
                entity.hasComments = stat.hasComments;
                entity.hasLinks = stat.hasLinks;
                entity.hasAssumptions = stat.hasAssumptions;
            }
        });
    }

    getOverlayById(overlayId: number): IOverlayModel {
        if (!overlayId) {
            return null;
        }
        return this.Overlays.find(overlay => overlay.Id === overlayId);
    }

    setCalibrationUsesTypeLabels(calibrationUsesTypeLabels: ICalibrationUsesTypeLabel[]) {
        this.CalibrationUsesTypeLabels = _.defaultTo(calibrationUsesTypeLabels, []);
    }

    initAllCalibrationSetTables() {
        [
            CalibrationSetCollectionName.CALIBRATION_ZONE_COMBINATION,
            CalibrationSetCollectionName.CALIBRATION_OVERLAY,
            CalibrationSetCollectionName.CALIBRATION_TYPOLOGY
        ].forEach(calibrationSet => {
            [
                TableType.CALIBRATION_GENERAL,
                TableType.CALIBRATION_USES,
                TableType.CALIBRATION_PARKING,
                TableType.CALIBRATION_USE_CAPACITY,
                TableType.CALIBRATION_BONUS
            ].forEach(tableType => {
                this.initCalibrationSetTable(tableType, calibrationSet);
            });
        });
    }

    clearValidationResults() {
        this.validationResult = new Map();
    }
}
